import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import AvatarChip from '../../../../../shared/avatar-chip';
import OrganizationsAutoComplete from '../../../../../shared/organizations-autocomplete';
import { useRef, useState } from 'react';
import { ActionErrorEntry } from '../decom-policy-utils.tsx';
import VacationResponderStep from './vacation-responder-step.tsx';
import SendEmailStep from './send-email-step.tsx';
import UserNameChangeStep from './user-name-change-step.tsx';
import SharedAutocomplete from '../../../../../shared/shared-autocomplete.tsx';
import SharedDrivesAutocomplete from '../../../../../shared/shared-drives-autocomplete.tsx';
import SharedDrivesBigQueryAutocomplete from '../../../../../shared/shared-drive-bigquery-autocomplete.tsx';
import { DecomPolicyFormValues } from '../decom-policy-form-layout.tsx';
import UserAvatar from '../../../../../shared/user-avatar.tsx';
import UsersAutocomplete from '../../../../../shared/users-autocomplete.tsx';
import { useUserSettings } from '../../../../../utils/use-user-settings.tsx';
import { gPanelTiers } from '../../../../../shared/gPanel-tiers.ts';
import GmailExportEnterpriseAlert from './gmail-export-enterprise-alert.tsx';
import VaultExportStep from './vault-export-step.tsx';
import { isVaultExportAction } from '../../../../../shared/decom-actions.tsx';
import { isFeatureEnabled } from '../../../../../utils/feature-flags.ts';
import VaultExportStepOld from './vault-export-step-old.tsx';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Clear from '@mui/icons-material/Clear';

interface DecomActionProps {
  control: Control<DecomPolicyFormValues>;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  watch: UseFormWatch<DecomPolicyFormValues>;
  errors: FieldErrors<DecomPolicyFormValues>;
  clearErrors: UseFormClearErrors<DecomPolicyFormValues>;
  trigger: UseFormTrigger<DecomPolicyFormValues>;
  setActionTypesWithErrors: React.Dispatch<
    React.SetStateAction<ActionErrorEntry[]>
  >;
  actionType: string;
  actionDisplayValue: string;
  day: number;
}

export const clearFieldError = (
  idx: string,
  fieldKey: string,
  setErrors: React.Dispatch<
    React.SetStateAction<{ id: string; errors: Record<string, any> }[]>
  >
) => {
  setErrors((prev) =>
    prev
      .map((item) => {
        if (item.id === idx && item.errors?.[fieldKey]) {
          const { [fieldKey]: _, ...restErrors } = item.errors;
          return Object.keys(restErrors).length === 0
            ? null
            : { ...item, errors: restErrors };
        }
        return item;
      })
      .filter(
        (item): item is { id: string; errors: Record<string, any> } =>
          item !== null
      )
  );
};

export default function DecomAction({
  setValue,
  watch,
  control,
  errors,
  actionType,
  setActionTypesWithErrors,
  clearErrors,
  trigger,
  day,
}: DecomActionProps) {
  const { currentTier, features } = useUserSettings();
  const uiDecomUpdateFeatureEnabled = isFeatureEnabled(
    'decommissioningUiUpdate',
    features
  );

  const VaultExportStepComponent = uiDecomUpdateFeatureEnabled
    ? VaultExportStep
    : VaultExportStepOld;
  const actionsData = watch(`actions.${day}.actions`) || [];
  const idx = actionsData.findIndex(
    (action) => action.actionType === actionType
  );

  const [sharedDriveUserInput, setSharedDriveUserInput] = useState('');

  const [autoCompleteResponseDestUser, setAutoCompleteResponseDestUser] =
    useState('');
  const useManagerEmail = watch(
    `actions.${day}.actions.${idx}.useManagerEmail`
  );
  const useSharedDrive = watch(`actions.${day}.actions.${idx}.useSharedDrive`);
  const destFolderId =
    watch(`actions.${day}.actions.${idx}.destFolderId`) || '';

  const radioSelection = useSharedDrive
    ? 'shared-drive'
    : useManagerEmail === true
    ? 'manager'
    : useManagerEmail === false
    ? 'custom'
    : '';
  const destFolderName =
    watch(`actions.${day}.actions.${idx}.destFolderName`) || '';

  const newOrgPath = watch(`actions.${day}.actions.${idx}.newOrgPath`) || '';
  const sendPassword =
    watch(`actions.${day}.actions.${idx}.isSendPassword`) || false;
  const userToNotify =
    watch(`actions.${day}.actions.${idx}.userToNotify`) || [];
  const destUser = watch(`actions.${day}.actions.${idx}.destUser`) || [];
  const autocompleteKey = useRef(new Date().getTime().toString());
  const autocompleteKeyDestUser = useRef(new Date().getTime().toString());
  const [
    autoCompleteResponseDefaultUsersToNotify,
    setAutoCompleteResponseDefaultUsersToNotify,
  ] = useState('');
  const selectedDestUserTypeRef = useRef<'user' | 'group'>('user');

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: `actions.${day}.actions.${idx}.destUser`,
  });
  const staticPassword =
    watch(`actions.${day}.actions.${idx}.staticPassword`) || false;

  const relationTypes = [
    { value: 'spouse', label: 'Spouse' },
    { value: 'child', label: 'Child' },
    { value: 'mother', label: 'Mother' },
    { value: 'father', label: 'Father' },
    { value: 'parent', label: 'Parent' },
    { value: 'brother', label: 'Brother' },
    { value: 'sister', label: 'Sister' },
    { value: 'friend', label: 'Friend' },
    { value: 'domestic_partner', label: 'Domestic Partner' },
    { value: 'manager', label: 'Manager' },
    { value: 'assistant', label: 'Assistant' },
    { value: 'referred_by', label: 'Referred By' },
    { value: 'partner', label: 'Partner' },
  ];

  const isEnterprise = currentTier === gPanelTiers.enterprise.value;

  const handleSetUserToNotify = (newEmail: string) => {
    if (newEmail && !userToNotify.includes(newEmail)) {
      const updatedUsers = [...userToNotify, newEmail];
      setValue(`actions.${day}.actions.${idx}.userToNotify`, updatedUsers, {
        shouldDirty: true,
      });
      clearErrors(`actions.${day}.actions.${idx}.userToNotify`);
      setValue(
        `actions.${day}.actions.${idx}.notifyUser`,
        updatedUsers.length > 0,
        {
          shouldDirty: true,
        }
      );
    }
    autocompleteKey.current = new Date().getTime().toString();
    setAutoCompleteResponseDefaultUsersToNotify('');
  };

  const handleDeleteUserToNotify = (email: string) => {
    const updatedUsers = userToNotify.filter((e: string) => e !== email);
    setValue(`actions.${day}.actions.${idx}.userToNotify`, updatedUsers, {
      shouldDirty: true,
    });
    setValue(
      `actions.${day}.actions.${idx}.notifyUser`,
      updatedUsers.length > 0,
      {
        shouldDirty: true,
      }
    );
  };

  const handleSharingScopeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: any
  ) => {
    const val = e.target.value as 'all' | 'external' | 'internal';
    field.onChange(val);
    setValue(`actions.${day}.actions.${idx}.sharingScope`, val, {
      shouldDirty: true,
    });
  };

  const handleMustAcknowledgeLogsChange = () => {
    setValue(
      `actions.${day}.actions.${idx}.mustAcknowledgeLogs`,
      !actionsData[idx].mustAcknowledgeLogs,
      { shouldDirty: true }
    );
  };

  const handleDestinationRadioChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: any
  ) => {
    const selected = e.target.value;
    if (selected === 'manager') {
      field.onChange(true);
      setValue(`actions.${day}.actions.${idx}.useManagerEmail`, true, {
        shouldValidate: true,
        shouldDirty: true,
      });
      remove();
      setValue(`actions.${day}.actions.${idx}.destFolderId`, '');
      setValue(`actions.${day}.actions.${idx}.destFolderName`, '');
      clearFieldError(actionType, 'useManagerEmail', setActionTypesWithErrors);
      clearFieldError(actionType, 'destUser', setActionTypesWithErrors);
      clearErrors(`actions.${day}.actions.${idx}.destUser`);
      clearErrors(`actions.${day}.actions.${idx}.destFolderId`);
    } else if (selected === 'custom') {
      field.onChange(false);
      setValue(`actions.${day}.actions.${idx}.useManagerEmail`, false, {
        shouldValidate: true,
        shouldDirty: true,
      });
      remove();
      setValue(`actions.${day}.actions.${idx}.destFolderId`, '');
      setValue(`actions.${day}.actions.${idx}.destFolderName`, '');
      clearErrors(`actions.${day}.actions.${idx}.destFolderId`);
      clearFieldError(actionType, 'useManagerEmail', setActionTypesWithErrors);
      clearErrors(`actions.${day}.actions.${idx}.destUser`);
      clearFieldError(actionType, 'destUser', setActionTypesWithErrors);
    } else if (selected === 'shared-drive') {
      field.onChange(false);
      setValue(`actions.${day}.actions.${idx}.useManagerEmail`, false, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`actions.${day}.actions.${idx}.useSharedDrive`, true, {
        shouldDirty: true,
      });
      clearErrors(`actions.${day}.actions.${idx}.destUser`);
      clearErrors(`actions.${day}.actions.${idx}.destFolderId`);
    }

    if (selected !== 'custom' && selected !== 'shared-drive') {
      remove();
    }
    if (selected !== 'shared-drive') {
      setValue(`actions.${day}.actions.${idx}.destFolderId`, '');
      setValue(`actions.${day}.actions.${idx}.destFolderName`, '');
      setValue(`actions.${day}.actions.${idx}.useSharedDrive`, false, {
        shouldDirty: true,
      });
    }
  };

  const handleDestUserAutoComplete = (newVal: string) => {
    if (fields.find((userDest) => userDest.userEmail === newVal)) return;
    if (newVal === '') return;
    append({ userEmail: newVal, role: 'owner', type: 'user' });
    clearFieldError(actionType, 'destUser', setActionTypesWithErrors);
    clearErrors(`actions.${day}.actions.${idx}.destUser`);
  };

  const handleDestUserDelete = (index: number) => {
    remove(index);
    clearFieldError(actionType, 'destUser', setActionTypesWithErrors);
    clearErrors(`actions.${day}.actions.${idx}.destUser`);
  };

  const handleSharedDriveUserDelete = () => {
    remove();
    setValue(`actions.${day}.actions.${idx}.destFolderId`, '');
    setValue(`actions.${day}.actions.${idx}.destFolderName`, '');
  };

  const handleSharedDriveUserAutoComplete = (newVal: string) => {
    setSharedDriveUserInput('');
    if (!newVal) return;
    if (fields.find((userDest) => userDest.userEmail === newVal)) return;
    append({ userEmail: newVal, role: 'owner', type: 'user' });
    clearFieldError(actionType, 'destUser', setActionTypesWithErrors);
    clearErrors(`actions.${day}.actions.${idx}.destUser`);
  };

  const handleDeleteDestFolder = () => {
    if (radioSelection === 'shared-drive') {
      setValue(`actions.${day}.actions.${idx}.destFolderId`, '', {
        shouldDirty: true,
      });
      setValue(`actions.${day}.actions.${idx}.destFolderName`, '', {
        shouldDirty: true,
      });
    }
  };

  const handleSelectDestFolder = (drive: any) => {
    setValue(`actions.${day}.actions.${idx}.destFolderId`, drive?.id ?? '', {
      shouldDirty: true,
    });
    setValue(
      `actions.${day}.actions.${idx}.destFolderName`,
      drive?.name ?? '',
      {
        shouldDirty: true,
      }
    );
  };

  const handleDaysUntilExpirationChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: any
  ) => {
    const value =
      event.target.value === '' ? null : parseInt(event.target.value);
    field.onChange(value);
    clearFieldError(
      actionType,
      'daysUntilExpiration',
      setActionTypesWithErrors
    );
    clearErrors(`actions.${day}.actions.${idx}.daysUntilExpiration`);
  };

  const handleReportRecipientRadioChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: any
  ) => {
    const selected = e.target.value;
    if (selected === 'manager') {
      field.onChange(true);
      setValue(`actions.${day}.actions.${idx}.useManagerEmail`, true, {
        shouldValidate: true,
        shouldDirty: true,
      });
      remove();
      clearFieldError(actionType, 'useManagerEmail', setActionTypesWithErrors);
      clearFieldError(actionType, 'destUser', setActionTypesWithErrors);
      clearErrors(`actions.${day}.actions.${idx}.destUser`);
    } else if (selected === 'custom') {
      field.onChange(false);
      setValue(`actions.${day}.actions.${idx}.useManagerEmail`, false, {
        shouldValidate: true,
        shouldDirty: true,
      });
      if (!destUser) {
        remove();
      }
      clearFieldError(actionType, 'useManagerEmail', setActionTypesWithErrors);
      clearErrors(`actions.${day}.actions.${idx}.destUser`);
      clearFieldError(actionType, 'destUser', setActionTypesWithErrors);
    } else {
      field.onChange(undefined);
      clearFieldError(actionType, 'destUser', setActionTypesWithErrors);
      clearErrors(`actions.${day}.actions.${idx}.destUser`);
    }
  };

  const handleReportRecipientAutoComplete = (value: string) => {
    setAutoCompleteResponseDestUser(value);
    clearErrors(`actions.${day}.actions.${idx}.destUser`);
    if (fields.find((userDest) => userDest.userEmail === value)) return;
    if (value === '') return;
    const hasOwner = fields.some((userDest) => userDest.role === 'owner');
    let roleToAssign: 'owner' | 'reader' = 'owner';
    if (selectedDestUserTypeRef.current === 'group' || hasOwner) {
      roleToAssign = 'reader';
    }
    append({
      userEmail: value,
      role: roleToAssign,
      type: selectedDestUserTypeRef.current,
    });
    setAutoCompleteResponseDestUser('');
    autocompleteKeyDestUser.current = new Date().getTime().toString();
    trigger(`actions.${day}.actions.${idx}.destUser`);
  };

  const handleDestUserRoleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    const newRole = e.target.value as
      | 'owner'
      | 'reader'
      | 'commenter'
      | 'writer';
    update(index, { ...fields[index], role: newRole });
    if (newRole === 'owner') {
      clearErrors(`actions.${day}.actions.${idx}.destUser`);
    }
  };

  const handleDestUserRoleDelete = (index: number) => {
    remove(index);
    clearErrors(`actions.${day}.actions.${idx}.destUser`);
  };

  const handleForceResetPasswordChange = () => {
    setValue(
      `actions.${day}.actions.${idx}.forceResetPassword`,
      !actionsData[idx].forceResetPassword,
      { shouldDirty: true }
    );
  };

  const handleSendPasswordChange = () => {
    setValue(`actions.${day}.actions.${idx}.isSendPassword`, !sendPassword, {
      shouldDirty: true,
    });
  };

  const handleStaticPasswordChange = () => {
    setValue(`actions.${day}.actions.${idx}.staticPassword`, !staticPassword, {
      shouldDirty: true,
    });
    setValue(`actions.${day}.actions.${idx}.newPassword`, '', {
      shouldDirty: true,
    });
  };

  const handleDelegateRelationInput = (newValue: string | null) => {
    if (newValue) {
      remove(0);
      append({ userEmail: newValue, role: 'owner', type: 'user' });
      clearFieldError(actionType, 'destUser', setActionTypesWithErrors);
      clearErrors(`actions.${day}.actions.${idx}.destUser`);
    } else {
      remove(0);
    }
  };

  const handleDelegateRelationChange = (
    newValue: string | { value: string; label: string } | null
  ) => {
    if (newValue && typeof newValue === 'string') {
      remove(0);
      append({ userEmail: newValue, role: 'owner', type: 'user' });
    } else if (newValue && typeof newValue !== 'string') {
      remove(0);
      append({ userEmail: newValue.value, role: 'owner', type: 'user' });
    } else {
      remove(0);
    }
    clearFieldError(actionType, 'destUser', setActionTypesWithErrors);
  };

  const handleOrgPathSelect = (newValue: string) => {
    setValue(`actions.${day}.actions.${idx}.newOrgPath`, newValue, {
      shouldDirty: true,
    });
    trigger(`actions.${day}.actions.${idx}.newOrgPath`);
    clearFieldError(actionType, 'newOrgPath', setActionTypesWithErrors);
  };

  const handleDeleteOrgPath = () => {
    setValue(`actions.${day}.actions.${idx}.newOrgPath`, '', {
      shouldDirty: true,
    });
  };

  const getDestinationLabel = () => {
    if (actionType === 'transfercals' || actionType === 'transferDocs')
      return 'Destination:';
    if (actionType === 'addDelegate' || actionType === 'EmailDump')
      return 'Destination User:';
    if (actionType === 'setForwarding' || actionType === 'ReassignNicknames')
      return 'Destination User/Group:';
    return 'Report Recipient Email:';
  };

  const getSharedDriveHelperText = () => {
    if (radioSelection !== 'shared-drive') return undefined;
    if (errors.actions?.[day]?.actions?.[idx]?.destUser) {
      return errors.actions?.[day]?.actions?.[idx]?.destUser?.message;
    }
    return 'This will transfer all documents to this user initially in a bundled folder, move said documents to the selected shared drive and delete the bundled folder from the initial user.';
  };

  const getSharedDriveFolderHelperText = () => {
    if (radioSelection !== 'shared-drive') return undefined;
    return errors.actions?.[day]?.actions?.[idx]?.destFolderId?.message;
  };

  const renderEmailsToNotify = () => {
    if (isVaultExportAction(actionType)) return null;
    return (
      <>
        <FormLabel sx={{ mt: 1 }}>Emails to Notify:</FormLabel>
        <Controller
          name={`actions.${day}.actions.${idx}.userToNotify`}
          control={control}
          render={() => (
            <Box sx={{ width: 400 }}>
              <SharedAutocomplete
                sources={['users', 'groups']}
                autocompleteValue={autoCompleteResponseDefaultUsersToNotify}
                setAutoCompleteValue={handleSetUserToNotify}
              />
            </Box>
          )}
        />
        {userToNotify?.length > 0 && (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {userToNotify.map((email: string) => (
              <AvatarChip
                key={email}
                type="Unknown"
                email={email}
                onDelete={() => handleDeleteUserToNotify(email)}
                size="small"
              />
            ))}
          </Box>
        )}
      </>
    );
  };

  const renderRemoveUsersShares = () => {
    if (actionType !== 'removeUsersShares') return null;
    return (
      <Controller
        name={`actions.${day}.actions.${idx}.sharingScope`}
        control={control}
        render={({ field }) => (
          <FormControl component="fieldset" sx={{ pt: 1 }}>
            <FormLabel component="legend">Sharing Scope:</FormLabel>
            <RadioGroup
              sx={{ ml: 2 }}
              value={field.value ?? 'all'}
              onChange={(e) => handleSharingScopeChange(e, field)}
            >
              <FormControlLabel
                value="all"
                control={<Radio />}
                label="All shares (Internal & External)"
              />
              <FormControlLabel
                value="external"
                control={<Radio />}
                label="External shares only"
              />
              <FormControlLabel
                value="internal"
                control={<Radio />}
                label="Internal shares only"
              />
            </RadioGroup>
          </FormControl>
        )}
      />
    );
  };

  const renderDeleteUser = () => {
    if (actionType !== 'deleteUser') return null;
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={actionsData[idx].mustAcknowledgeLogs === true}
            onChange={handleMustAcknowledgeLogsChange}
          />
        }
        label="Must acknowledge logs before deletion"
      />
    );
  };

  const renderVaultGmailExport = () => {
    if (actionType !== 'vaultGmailExport') return null;
    return (
      <>
        {!isEnterprise && (
          <Box sx={{ pt: 1 }}>
            <GmailExportEnterpriseAlert />
          </Box>
        )}
        <VaultExportStepComponent
          watch={watch}
          setValue={setValue}
          clearErrors={clearErrors}
          errors={errors}
          day={day}
          control={control}
          idx={idx}
          actionType={actionType}
          setActionTypesWithErrors={setActionTypesWithErrors}
          destUserFields={fields}
          destUserAppend={append}
          destUserRemove={remove}
          destUserUpdate={update}
        />
      </>
    );
  };

  const renderVaultCalendarExport = () => {
    if (actionType !== 'vaultCalendarExport') return null;
    return (
      <VaultExportStepComponent
        watch={watch}
        setValue={setValue}
        clearErrors={clearErrors}
        errors={errors}
        day={day}
        control={control}
        idx={idx}
        actionType={actionType}
        setActionTypesWithErrors={setActionTypesWithErrors}
        destUserFields={fields}
        destUserAppend={append}
        destUserRemove={remove}
        destUserUpdate={update}
      />
    );
  };

  const renderVaultChatExport = () => {
    if (actionType !== 'vaultChatExport') return null;
    return (
      <VaultExportStepComponent
        watch={watch}
        setValue={setValue}
        clearErrors={clearErrors}
        errors={errors}
        day={day}
        control={control}
        idx={idx}
        actionType={actionType}
        setActionTypesWithErrors={setActionTypesWithErrors}
        destUserFields={fields}
        destUserAppend={append}
        destUserRemove={remove}
        destUserUpdate={update}
      />
    );
  };

  const renderVaultDriveExport = () => {
    if (actionType !== 'vaultDriveExport') return null;
    return (
      <VaultExportStepComponent
        watch={watch}
        setValue={setValue}
        clearErrors={clearErrors}
        errors={errors}
        day={day}
        control={control}
        idx={idx}
        actionType={actionType}
        setActionTypesWithErrors={setActionTypesWithErrors}
        destUserFields={fields}
        destUserAppend={append}
        destUserRemove={remove}
        destUserUpdate={update}
      />
    );
  };

  const renderVaultGeminiExport = () => {
    if (actionType !== 'vaultGeminiExport') return null;
    return (
      <VaultExportStepComponent
        watch={watch}
        setValue={setValue}
        clearErrors={clearErrors}
        errors={errors}
        day={day}
        control={control}
        idx={idx}
        actionType={actionType}
        setActionTypesWithErrors={setActionTypesWithErrors}
        destUserFields={fields}
        destUserAppend={append}
        destUserRemove={remove}
        destUserUpdate={update}
      />
    );
  };

  const renderVaultGroupsExport = () => {
    if (actionType !== 'vaultGroupsExport') return null;
    return (
      <VaultExportStepComponent
        watch={watch}
        setValue={setValue}
        clearErrors={clearErrors}
        errors={errors}
        day={day}
        control={control}
        idx={idx}
        actionType={actionType}
        setActionTypesWithErrors={setActionTypesWithErrors}
        destUserFields={fields}
        destUserAppend={append}
        destUserRemove={remove}
        destUserUpdate={update}
      />
    );
  };

  const renderCustomUserField = () => {
    if (radioSelection !== 'custom') {
      return (
        <UsersAutocomplete
          autocompleteValue={''}
          setAutoCompleteValue={() => {}}
          errors={false}
          helperText={undefined}
          disabled={true}
        />
      );
    }
    if (!Array.isArray(destUser) || destUser.length === 0) {
      return (
        <UsersAutocomplete
          autocompleteValue={''}
          setAutoCompleteValue={handleDestUserAutoComplete}
          errors={!!errors.actions?.[day]?.actions?.[idx]?.destUser}
          helperText={
            errors.actions?.[day]?.actions?.[idx]?.destUser
              ? errors.actions?.[day]?.actions?.[idx]?.destUser.message
              : undefined
          }
          disabled={false}
        />
      );
    }
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          flexWrap: 'wrap',
          gap: 1,
          mt: 1,
          ml: 0,
        }}
      >
        {destUser.map((user: any, index: number) => (
          <AvatarChip
            key={user.userEmail}
            type="User"
            email={user.userEmail || ''}
            onDelete={() => handleDestUserDelete(index)}
            size="small"
          />
        ))}
      </Box>
    );
  };

  const renderDestinationSettings = () => {
    const isDestinationAction =
      (actionType === 'EmailDump' && isEnterprise) ||
      [
        'transfercals',
        'transferDocs',
        'addDelegate',
        'setForwarding',
        'ReassignNicknames',
      ].includes(actionType);

    if (!isDestinationAction) return null;

    return (
      <>
        <Controller
          name={`actions.${day}.actions.${idx}.useManagerEmail`}
          control={control}
          defaultValue={undefined}
          render={({ field }) => (
            <FormControl
              component="fieldset"
              sx={{ pt: 1 }}
              error={!!errors?.actions?.[day]?.actions?.[idx]?.useManagerEmail}
            >
              <FormLabel component="legend">{getDestinationLabel()}</FormLabel>
              {!!errors?.actions?.[day]?.actions?.[idx]?.useManagerEmail && (
                <FormHelperText>
                  {
                    errors?.actions?.[day]?.actions?.[idx]?.useManagerEmail
                      .message
                  }
                </FormHelperText>
              )}
              <RadioGroup
                value={radioSelection}
                onChange={(e) => handleDestinationRadioChange(e, field)}
              >
                <FormControlLabel
                  value="manager"
                  control={<Radio />}
                  label="Manager"
                />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: destUser === null ? 'start' : 'center',
                    gap: 1,
                  }}
                >
                  <FormControlLabel
                    value="custom"
                    control={<Radio />}
                    label="Custom User:"
                  />
                  <Controller
                    name={`actions.${day}.actions.${idx}.destUser`}
                    control={control}
                    render={() => (
                      <Box sx={{ width: 400 }}>{renderCustomUserField()}</Box>
                    )}
                  />
                </Box>
                {actionType === 'transferDocs' && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'start',
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    <FormControlLabel
                      value="shared-drive"
                      control={<Radio />}
                      sx={{ width: 'fit-content' }}
                      label="Shared Drive:"
                    />
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      <Controller
                        name={`actions.${day}.actions.${idx}.destUser`}
                        control={control}
                        render={() => (
                          <Box sx={{ width: 400 }}>
                            {radioSelection === 'shared-drive' &&
                            destUser[0]?.userEmail ? (
                              <>
                                <AvatarChip
                                  type="User"
                                  email={destUser[0].userEmail}
                                  onDelete={handleSharedDriveUserDelete}
                                />
                                {!errors.actions?.[day]?.actions?.[idx]
                                  ?.destUser && (
                                  <FormHelperText>
                                    This will transfer all documents to this
                                    user initially in a bundled folder, move
                                    said documents to the selected shared drive
                                    and delete the bundled folder from the
                                    initial user.
                                  </FormHelperText>
                                )}
                              </>
                            ) : (
                              <UsersAutocomplete
                                autocompleteValue={sharedDriveUserInput}
                                setAutoCompleteValue={
                                  handleSharedDriveUserAutoComplete
                                }
                                errors={
                                  radioSelection === 'shared-drive' &&
                                  !!errors.actions?.[day]?.actions?.[idx]
                                    ?.destUser
                                }
                                helperText={getSharedDriveHelperText()}
                                disabled={radioSelection !== 'shared-drive'}
                              />
                            )}
                          </Box>
                        )}
                      />
                      <Controller
                        name={`actions.${day}.actions.${idx}.destFolderId`}
                        control={control}
                        render={() => (
                          <Box sx={{ width: 400 }}>
                            {destFolderId &&
                            radioSelection === 'shared-drive' ? (
                              <AvatarChip
                                type="Shared Drive"
                                email={destFolderName}
                                onDelete={handleDeleteDestFolder}
                              />
                            ) : !isEnterprise ? (
                              <SharedDrivesAutocomplete
                                autocompleteValue={destFolderId}
                                setAutoCompleteValue={(value) => {
                                  setValue(
                                    `actions.${day}.actions.${idx}.destFolderId`,
                                    value,
                                    {
                                      shouldDirty: true,
                                    }
                                  );
                                }}
                                onDriveSelect={handleSelectDestFolder}
                                size="small"
                                errors={
                                  radioSelection === 'shared-drive' &&
                                  !!errors.actions?.[day]?.actions?.[idx]
                                    ?.destFolderId
                                }
                                helperText={getSharedDriveFolderHelperText()}
                                disabled={
                                  radioSelection !== 'shared-drive' || !destUser
                                }
                              />
                            ) : (
                              <SharedDrivesBigQueryAutocomplete
                                autocompleteValue={destFolderId}
                                setAutoCompleteValue={(value) => {
                                  setValue(
                                    `actions.${day}.actions.${idx}.destFolderId`,
                                    value,
                                    {
                                      shouldDirty: true,
                                    }
                                  );
                                }}
                                onDriveSelect={handleSelectDestFolder}
                                size="small"
                                errors={
                                  radioSelection === 'shared-drive' &&
                                  !!errors.actions?.[day]?.actions?.[idx]
                                    ?.destFolderId
                                }
                                helperText={getSharedDriveFolderHelperText()}
                                disabled={
                                  radioSelection !== 'shared-drive' || !destUser
                                }
                              />
                            )}
                          </Box>
                        )}
                      />
                    </Stack>
                  </Box>
                )}
              </RadioGroup>
            </FormControl>
          )}
        />

        {actionType === 'addDelegate' && (
          <Controller
            name={`actions.${day}.actions.${idx}.daysUntilExpiration`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                onChange={(event) =>
                  handleDaysUntilExpirationChange(event, field)
                }
                label="Days Until Expiration"
                error={
                  !!errors?.actions?.[day]?.actions?.[idx]?.daysUntilExpiration
                }
                helperText={
                  errors?.actions?.[day]?.actions?.[idx]?.daysUntilExpiration
                    ? errors?.actions?.[day]?.actions?.[idx]
                        ?.daysUntilExpiration?.message
                    : ' '
                }
                type="number"
                slotProps={{
                  input: {
                    inputProps: { min: 1, max: 365 },
                  },
                  formHelperText: {
                    sx: { textWrap: 'nowrap' },
                  },
                }}
                sx={{ width: 230, mt: 1 }}
              />
            )}
          />
        )}
      </>
    );
  };

  const renderReportRecipientSettings = () => {
    const isReportAction = [
      'actSharedExternallyReportMyDrive',
      'actSharedExternallyReportSharedDrive',
      'actSharedPubliclyReportMyDrive',
      'actSharedPubliclyReportSharedDrives',
      'docsSharedExternallyReport',
      'removeExternalShares',
      'RemoveFromAllSharedDrive',
    ].includes(actionType);

    if (!isReportAction) return null;

    return (
      <Controller
        name={`actions.${day}.actions.${idx}.useManagerEmail`}
        control={control}
        defaultValue={undefined}
        render={({ field }) => (
          <FormControl
            component="fieldset"
            sx={{ pt: 1 }}
            error={!!errors?.actions?.[day]?.actions?.[idx]?.useManagerEmail}
          >
            <FormLabel component="legend">Report Recipient Email:</FormLabel>
            {!!errors?.actions?.[day]?.actions?.[idx]?.useManagerEmail && (
              <FormHelperText>
                {
                  errors?.actions?.[day]?.actions?.[idx]?.useManagerEmail
                    .message
                }
              </FormHelperText>
            )}
            <RadioGroup
              value={radioSelection}
              onChange={(e) => handleReportRecipientRadioChange(e, field)}
            >
              <FormControlLabel
                value="manager"
                control={<Radio />}
                sx={{ width: 'fit-content' }}
                label="Manager"
              />
              <Stack direction="row" alignItems="flex-start">
                <FormControlLabel
                  value="custom"
                  control={<Radio />}
                  sx={{ width: 'fit-content', mt: 0 }}
                  label="Custom User or Group:"
                />
                <Controller
                  name={`actions.${day}.actions.${idx}.destUser`}
                  control={control}
                  render={() => (
                    <Box
                      sx={{
                        width: 300,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box sx={{ height: 56 }}>
                        <SharedAutocomplete
                          sources={['users', 'groups']}
                          autocompleteValue={autoCompleteResponseDestUser}
                          setAutoCompleteTypeValue={(value: string) => {
                            if (value === 'user' || value === 'group') {
                              selectedDestUserTypeRef.current = value;
                            }
                          }}
                          setAutoCompleteValue={
                            handleReportRecipientAutoComplete
                          }
                          errors={
                            !!errors.actions?.[day]?.actions?.[idx]?.destUser
                          }
                          disabled={radioSelection !== 'custom'}
                          helperText={
                            errors.actions?.[day]?.actions?.[idx]?.destUser
                              ?.message || ' '
                          }
                        />
                      </Box>
                    </Box>
                  )}
                />
              </Stack>
              <List sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }}>
                {fields &&
                  fields.map((userDest, index) => {
                    const hasOwner = fields.some((u) => u.role === 'owner');
                    return (
                      <Stack key={userDest.userEmail}>
                        <ListItem
                          dense
                          sx={{ maxWidth: 600 }}
                          secondaryAction={
                            <Stack
                              flexDirection="row"
                              gap={1}
                              alignItems="center"
                            >
                              <TextField
                                value={userDest.role}
                                onChange={(e) =>
                                  handleDestUserRoleChange(e, index)
                                }
                                select
                                label="Role"
                                size="small"
                                sx={{ width: 175 }}
                                slotProps={{
                                  inputLabel: { htmlFor: `role-${index}` },
                                  htmlInput: { id: `role-${index}` },
                                }}
                              >
                                {userDest.type !== 'group' && (
                                  <MenuItem
                                    key={`owner-${userDest.userEmail}`}
                                    value="owner"
                                    disabled={
                                      hasOwner && userDest.role !== 'owner'
                                    }
                                  >
                                    Owner
                                  </MenuItem>
                                )}
                                <MenuItem
                                  key={`reader-${userDest.userEmail}`}
                                  value="reader"
                                >
                                  Viewer
                                </MenuItem>
                                <MenuItem
                                  key={`commenter-${userDest.userEmail}`}
                                  value="commenter"
                                >
                                  Commenter
                                </MenuItem>
                                <MenuItem
                                  key={`writer-${userDest.userEmail}`}
                                  value="writer"
                                >
                                  Editor
                                </MenuItem>
                              </TextField>
                              <Tooltip arrow title="Remove">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleDestUserRoleDelete(index)
                                  }
                                >
                                  <Clear />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          }
                        >
                          <ListItemAvatar>
                            <UserAvatar userEmail={userDest.userEmail || ''} />
                          </ListItemAvatar>
                          <ListItemText
                            primary={userDest.userEmail}
                            slotProps={{
                              primary: {
                                maxWidth: 300,
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                              },
                            }}
                          />
                        </ListItem>
                      </Stack>
                    );
                  })}
              </List>
            </RadioGroup>
          </FormControl>
        )}
      />
    );
  };

  const renderUserNameChange = () => {
    if (actionType !== 'UserNameChange') return null;
    return (
      <UserNameChangeStep
        control={control}
        watch={watch}
        setValue={setValue}
        day={day}
        idx={idx}
      />
    );
  };

  const renderResetPassword = () => {
    if (actionType !== 'ResetPassword') return null;
    return (
      <>
        <FormControlLabel
          control={
            <Checkbox
              checked={actionsData[idx].forceResetPassword === true}
              onChange={handleForceResetPasswordChange}
            />
          }
          label="Force Reset Password"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={sendPassword}
              onChange={handleSendPasswordChange}
            />
          }
          label="Include in Notification Email"
        />
        <FormControl>
          <FormLabel id="reset-password-radio-buttons-label">
            Password Type
          </FormLabel>
          <RadioGroup
            aria-labelledby="reset-password-radio-buttons-group-label"
            name="reset-password-radio-buttons-group"
            value={staticPassword}
            onChange={handleStaticPasswordChange}
            row
          >
            <FormControlLabel
              value={false}
              control={<Radio />}
              label="Random"
            />
            <FormControlLabel
              value={true}
              control={<Radio />}
              label="User Defined"
            />
          </RadioGroup>
        </FormControl>
        <Controller
          name={`actions.${day}.actions.${idx}.newPassword`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="User Defined"
              disabled={!staticPassword}
              error={!!errors?.actions?.[day]?.actions?.[idx]?.newPassword}
              helperText={
                errors.actions?.[day]?.actions?.[idx]?.newPassword
                  ? errors?.actions?.[day]?.actions?.[idx]?.newPassword?.message
                  : ' '
              }
              sx={{ flexGrow: 1 }}
              autoComplete="off"
            />
          )}
        />
      </>
    );
  };

  const renderDelegateRelation = () => {
    if (actionType !== 'delegateToRelation') return null;
    return (
      <>
        <Typography sx={{ pt: 1, pb: 1 }}>Delegate Relation:</Typography>
        <Controller
          name={`actions.${day}.actions.${idx}.destUser`}
          control={control}
          render={() => (
            <Autocomplete
              size="medium"
              options={relationTypes}
              autoComplete
              autoHighlight
              freeSolo
              sx={{ width: 400 }}
              inputValue={destUser[0]?.userEmail || ''}
              onInputChange={(_event, newValue) =>
                handleDelegateRelationInput(newValue)
              }
              onChange={(_event, newValue) =>
                handleDelegateRelationChange(newValue)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Delegate Relation"
                  fullWidth
                  error={!!errors?.actions?.[day]?.actions?.[idx]?.destUser}
                  helperText={
                    errors?.actions?.[day]?.actions?.[idx]?.destUser
                      ? errors?.actions?.[day]?.actions?.[idx]?.destUser
                          ?.message
                      : ' '
                  }
                />
              )}
              renderOption={(props, option) => (
                <ListItem {...props} key={option.value}>
                  {option.label}
                </ListItem>
              )}
            />
          )}
        />
        <Controller
          name={`actions.${day}.actions.${idx}.daysUntilExpiration`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value || ''}
              onChange={(event) =>
                handleDaysUntilExpirationChange(event, field)
              }
              label="Days Until Expiration"
              error={
                !!errors?.actions?.[day]?.actions?.[idx]?.daysUntilExpiration
              }
              helperText={
                errors.actions?.[day]?.actions?.[idx]?.daysUntilExpiration
                  ? errors?.actions?.[day]?.actions?.[idx]?.daysUntilExpiration
                      ?.message
                  : ' '
              }
              type="number"
              slotProps={{
                input: { inputProps: { min: 1, max: 365 } },
                formHelperText: { sx: { textWrap: 'nowrap' } },
              }}
              sx={{ width: 180, mt: 1 }}
            />
          )}
        />
      </>
    );
  };

  const renderUserOrgChange = () => {
    if (actionType !== 'UserOrgChange') return null;
    return (
      <>
        <Typography sx={{ py: 1 }}>New Org Path:</Typography>
        {actionsData[idx].newOrgPath &&
        actionsData[idx].newOrgPath.length > 0 ? (
          <AvatarChip
            type="OrgUnit"
            email={actionsData[idx].newOrgPath}
            onDelete={handleDeleteOrgPath}
          />
        ) : (
          <Controller
            name={`actions.${day}.actions.${idx}.newOrgPath`}
            control={control}
            render={() => (
              <Box sx={{ width: 400 }}>
                <OrganizationsAutoComplete
                  autoCompleteValue={newOrgPath}
                  setAutoCompleteValue={handleOrgPathSelect}
                  hasError={
                    !!errors?.actions?.[day]?.actions?.[idx]?.newOrgPath
                  }
                  helperText={
                    errors.actions?.[day]?.actions?.[idx]?.newOrgPath
                      ? errors?.actions?.[day]?.actions?.[idx]?.newOrgPath
                          ?.message
                      : ' '
                  }
                />
              </Box>
            )}
          />
        )}
      </>
    );
  };

  const renderVacationResponder = () => {
    if (actionType !== 'vacationResponder') return null;
    return (
      <VacationResponderStep
        control={control}
        watch={watch}
        clearErrors={clearErrors}
        setValue={setValue}
        setActionTypesWithErrors={setActionTypesWithErrors}
        errors={errors}
        day={day}
      />
    );
  };

  const renderSendEmail = () => {
    if (actionType !== 'sendEmail') return null;
    return (
      <SendEmailStep
        control={control}
        watch={watch}
        clearErrors={clearErrors}
        setActionTypesWithErrors={setActionTypesWithErrors}
        setValue={setValue}
        errors={errors}
        trigger={trigger}
        day={day}
      />
    );
  };

  return (
    <Stack spacing={1}>
      <Stack spacing={1}>
        <Stack direction="column" spacing={1} alignItems="flex-start">
          {actionType === 'RemoveFromAllSharedDrive' && (
            <Alert severity="info" sx={{ width: '100%' }}>
              Please note: we are not able to remove{' '}
              <Box component="span" sx={{ fontWeight: 'bold' }}>
                archived users
              </Box>{' '}
              from their Shared Drives.
            </Alert>
          )}
          {renderEmailsToNotify()}
        </Stack>
      </Stack>
      {renderRemoveUsersShares()}
      {renderDeleteUser()}
      {renderVaultGmailExport()}
      {renderVaultCalendarExport()}
      {renderVaultChatExport()}
      {renderVaultDriveExport()}
      {renderVaultGeminiExport()}
      {renderVaultGroupsExport()}
      {renderDestinationSettings()}
      {renderReportRecipientSettings()}
      {renderUserNameChange()}
      {renderResetPassword()}
      {renderDelegateRelation()}
      {renderUserOrgChange()}
      {renderVacationResponder()}
      {renderSendEmail()}
    </Stack>
  );
}
